import { createFileRoute, useRouter } from '@tanstack/react-router';
import { createAuthContextProvider } from '../../../utils/auth';
import {
  Box,
  Button,
  darkThemeClass,
  ScrollAreaRoot,
  ScrollAreaScrollbar,
  ScrollAreaViewport,
  Text,
} from '@streamelements/frontend-ui';
import { getUserConversionState } from '../_ensure-version.index/-utils';
import { Card } from '../../../components/Card';
import { ensureOBSMinVersion, ensureOBSPresence } from '../../../utils/ensureOBSPresence';
import { promisefy } from '../../../utils/promisefy';
// import { isLocalHost } from '../../../utils/isLocalHost';
import { navigateInModal } from '../../../utils/navigateInModal';
import { ServiceEnvironment } from '@streamelements/client-sdk-js';
import { channelsOwnerAccessQueryOptions } from '../../../api/channels';
import { OnboardingScopedStorage } from '../-onboardingScopedStorage';

// async function mountMultiStreamingWidget() {
//   const docks = await promisefy(window.host.getAllDockingWidgets)();

//   if (docks['streamelements_obslive_widgets_multistreaming']) {
//     return;
//   }

//   return promisefy(window.host.addDockingWidget)({
//     id: 'streamelements_obslive_widgets_multistreaming',
//     url: isLocalHost() ? `http://${window.location.hostname}:3000` : 'https://selive.streamelements.com',
//     title: 'StreamElements Multi-Streaming',
//     minWidth: 260,
//     minHeight: 260,
//     dockingArea: 'bottom',
//     visible: true,
//   });
// }

/**
 * Navigating to the old page (yoink codebase) will ensure that all the widgets are mounted
 * without us having to port over the code and potentially experience breaking changes and bugs.
 * Seems like a decent trade-off for now.
 */
async function navigateToLegacyLoginPage({ serviceEnvironment }: { serviceEnvironment: ServiceEnvironment }) {
  const seLiveWelcomePage = new URL(serviceEnvironment.SELiveBaseUrl);

  if (seLiveWelcomePage.origin !== 'localhost') {
    seLiveWelcomePage.pathname = '/welcome';
  } else {
    seLiveWelcomePage.pathname = '/obs-live';
  }

  window.location.href = seLiveWelcomePage.toString();
  // Prevent further code from being executed while navigating away
  await new Promise((resolve) => setTimeout(resolve, 3000));
}

export const Route = createFileRoute('/_authenticated/onboarding/getting-started')({
  component: OnboardingGettingStarted,
  beforeLoad: async ({ context: { serviceEnvironment } }) => {
    const authContextProvider = await createAuthContextProvider(serviceEnvironment);

    return {
      authContextProvider,
    };
  },
  loader: async ({ context: { serviceEnvironment, queryClient, authContextProvider } }) => {
    const { seLiveMultiStreamingMinVersionOk } = await ensureOBSMinVersion({
      minVersion: [25, 1, 9, 335],
    });

    if (!seLiveMultiStreamingMinVersionOk) {
      console.log('Skipping onboarding because SE.Live version does not match');
      await navigateToLegacyLoginPage({ serviceEnvironment });
    }

    const onboardingPreferences = await new OnboardingScopedStorage({
      authContextProvider,
      serviceEnvironment,
    }).getOnboardingPreferences();

    const { isOnboardingSkipped } = onboardingPreferences;

    if (isOnboardingSkipped) {
      console.info('Skipping onboarding because it was already skipped');
      await navigateToLegacyLoginPage({ serviceEnvironment });
    }

    const result = await getUserConversionState();

    const access = await queryClient.ensureQueryData(
      channelsOwnerAccessQueryOptions({
        authContextProvider,
        serviceEnvironment,
      })
    );

    const { hasCreatedSources } = result;
    const hasMultipleChannels = access.length > 1;

    if (hasCreatedSources && hasMultipleChannels) {
      await ensureOBSPresence();

      navigateToLegacyLoginPage({ serviceEnvironment });
      // Prevent the page from being loaded before the redirect
      await new Promise((resolve) => setTimeout(resolve, 3000));
    }

    return {
      ...result,
      hasMultipleChannels,
    };
  },
});

// function SvgPattern({ count = 23, style }: { count?: number; style: CSSProperties }) {
//   return (
//     <g style={style}>
//       {Array.from({ length: count }).map((_, index, { length: total }) => {
//         // i = 0; x = 100%
//         // i = 10; x = -100%
//         const x = index * 3.5;
//         // i = 0; y = 0%
//         // i = 10; y = 100%
//         const y = 100 - index * 3.5;
//         // i = 0; opacity = 0;
//         // i = total / 2; opacity = 1;
//         // i = total; opacity = 0
//         const midpoint = total / 1.5;
//         const opacity = index <= midpoint ? index / midpoint : 1 - (index - midpoint) / (total - midpoint);

//         return (
//           <Fragment key={index}>
//             <rect x={x} y={y} width="17.5" height="17.5" fill="#150089" key={index} />
//             <rect
//               x={x}
//               y={y}
//               width="17.5"
//               height="17.5"
//               fill="black"
//               key={index}
//               opacity={Math.min(opacity + 0.1, 1)}
//             />
//           </Fragment>
//         );
//         // <rect x="-18.75%" y="18.75%" width="100%" height="100%" fill="#150089" opacity={0.3} />
//         // <rect x="-37.5%" y="37.5%" width="100%" height="100%" fill="#150089" opacity={0.3} />
//       })}
//     </g>
//   );
// }

function OnboardingGettingStarted() {
  const { serviceEnvironment, authContextProvider } = Route.useRouteContext();
  const { hasCreatedSources, hasMultipleChannels } = Route.useLoaderData();
  const router = useRouter();

  async function onSkipClicked() {
    console.info('User skipped onboarding, navigating to legacy login page');
    await new OnboardingScopedStorage({
      authContextProvider,
      serviceEnvironment,
    }).skipOnboarding();
    await navigateToLegacyLoginPage({ serviceEnvironment });
  }

  async function mountOnboardingWidget() {
    const route = router.buildLocation({
      to: '/onboarding',
    });

    const url = new URL(route.href, window.location.origin);
    await promisefy(window.host.removeDockingWidgetsByIds)(['streamelements_multistreaming_onboarding']);

    await promisefy(window.host.addDockingWidget)({
      id: 'streamelements_multistreaming_onboarding',
      dockingArea: 'floating',
      title: 'SE.Live Setup Wizard',
      url: url.toString(),
      visible: false,
      minWidth: 300,
      minHeight: 650,
    });

    navigateToLegacyLoginPage({ serviceEnvironment });
  }

  async function onNewSetupClicked() {
    const result = await navigateInModal<boolean>({
      to: '/onboarding/new-setup',
      title: 'SE.Live Setup Wizard',
      width: 300,
      height: 650,
      isResizable: true,
    });

    if (!result) return;

    await mountOnboardingWidget();
  }

  async function onMultiStreamingSetupClicked() {
    const result = await navigateInModal<boolean>({
      to: '/onboarding/setup-multistreaming',
      title: 'SE.Live Setup Wizard',
      width: 300,
      height: 650,
      isResizable: true,
    });

    if (!result) return;

    await mountOnboardingWidget();
  }

  return (
    <Box
      css={{
        width: '100vw',
        flex: 1,
        overflow: 'hidden',
        backgroundColor: 'black',
        position: 'relative',
      }}
      className={darkThemeClass}
    >
      <Box
        css={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          backgroundImage: 'url("/onboarding-bg.jpg")',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          width: '100%',
          height: '100%',
        }}
      >
        {/* <Box as="svg" viewBox="0 0 100 100" fill="none" x={0} y={0}>
          <SvgPattern count={30} style={{ transform: 'translate(-32.5%, -35%)' }} />
          <SvgPattern count={23} style={{ transform: 'translate(20%, -30%)' }} />
          <SvgPattern count={23} style={{ transform: 'translate(-1.0625%, -35%)' }} />
        </Box> */}
      </Box>
      <ScrollAreaRoot css={{ overflow: 'hidden' }} type="always">
        <ScrollAreaViewport css={{ pt: '2.5rem' }}>
          <Text.Subtitle
            weight="bold"
            css={{
              mb: '0.25rem',
              maxWidth: 470,
              mx: 'auto',
              textAlign: 'center',
            }}
          >
            Welcome to SE.Live
          </Text.Subtitle>
          <Text.Heading
            level={1}
            weight="bold"
            css={{
              fontSize: '2.125rem',
              maxWidth: 470,
              mx: 'auto',
              textAlign: 'center',
              mb: '2rem',
            }}
          >
            I'd like to start with...
          </Text.Heading>

          <Box
            css={{
              display: 'flex',
              justifyContent: 'center',
              gap: '1rem',
              width: '100%',
              mb: '3.125rem',
            }}
          >
            {hasMultipleChannels ? null : (
              <Box
                css={{
                  width: '100%',
                  maxWidth: 210,
                  textAlign: 'center',
                  display: 'grid',
                  gap: '0.5rem',
                }}
              >
                <Card
                  css={{
                    padding: '1rem',
                    width: '100%',
                    textAlign: 'center',
                    m: 0,
                  }}
                  asChild
                >
                  <button onClick={onMultiStreamingSetupClicked}>
                    <img src="/onboarding-multistreaming-setup.png" alt="Multi-streaming setup" height={99} />
                    <Text.Subtitle weight="bold">
                      Multi-streaming <br /> Setup
                    </Text.Subtitle>
                  </button>
                </Card>
                <Text.Body css={{ color: '$textPrimary70' }} variant="caption">
                  For ALL creators with multiple channels
                </Text.Body>
              </Box>
            )}

            {hasCreatedSources ? null : (
              <Box
                css={{
                  width: '100%',
                  maxWidth: 210,
                  textAlign: 'center',
                  display: 'grid',
                  gap: '0.5rem',
                }}
              >
                <Card
                  css={{
                    padding: '1rem',
                    width: '100%',
                    textAlign: 'center',
                    m: 0,
                  }}
                  asChild
                >
                  <button onClick={onNewSetupClicked}>
                    <img src="/onboarding-new-setup.png" alt="New setup" height={99} />
                    <Text.Subtitle weight="bold">
                      New <br /> setup
                    </Text.Subtitle>
                  </button>
                </Card>
                <Text.Body css={{ color: '$textPrimary70' }} variant="caption">
                  Best for 1st time live streamers or new content creators
                </Text.Body>
              </Box>
            )}
          </Box>

          <Box
            css={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Text.Body weight="bold" css={{ textAlign: 'center' }}>
              Not sure what to start with? No worries.
            </Text.Body>
            <Text.Body
              css={{
                textAlign: 'center',
                color: '$textPrimary50',
                mb: '0.625rem',
              }}
              variant="caption"
            >
              You can always find these guides under “Streamelements” on the top menu
            </Text.Body>

            <Button density="compact" variant="outlined" onClick={onSkipClicked}>
              Skip
            </Button>
          </Box>
        </ScrollAreaViewport>
        <ScrollAreaScrollbar />
      </ScrollAreaRoot>
    </Box>
  );
}
